import { create } from 'zustand';
import type { CurrentUser } from '@core/types/models';
import { tokenStorage } from '@features/auth/utils/tokenStorage';
import { authService } from '@features/auth/services/auth.service';
import { UNAUTHORIZED_EVENT } from '@core/network/apiClient';
import { logger } from '@core/utils/logger';

interface AuthState {
  user: CurrentUser | null;
  /** False until the initial session check completes (prevents login-flash). */
  isInitialized: boolean;
  isAuthenticated: boolean;
  setSession: (user: CurrentUser, token: string) => void;
  initialize: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isInitialized: false,
  isAuthenticated: false,

  setSession: (user, token) => {
    tokenStorage.setAccessToken(token);
    set({ user, isAuthenticated: true });
  },

  initialize: async () => {
    if (!tokenStorage.hasToken()) {
      set({ isInitialized: true, user: null, isAuthenticated: false });
      return;
    }
    try {
      const user = await authService.getSession();
      set({ user, isAuthenticated: true, isInitialized: true });
    } catch {
      tokenStorage.clear();
      set({ user: null, isAuthenticated: false, isInitialized: true });
    }
  },

  logout: async () => {
    await authService.logout();
    tokenStorage.clear();
    set({ user: null, isAuthenticated: false });
  },
}));

// Global 401 handler → drop the session (guards redirect to /login on next render).
if (typeof window !== 'undefined') {
  window.addEventListener(UNAUTHORIZED_EVENT, () => {
    logger.info('Session expired — clearing auth state');
    tokenStorage.clear();
    useAuthStore.setState({ user: null, isAuthenticated: false });
  });
}
